import { getDefaultStore } from "jotai"
import { productsDictAtom, productsByBarcodeDictAtom } from "../global/AtomsProvider"
import { getDailyStatID } from "./timezoneFunctions"
import { calculateTotalPurchasePriceFromIngredients } from "./ingredientCalculations"

export const validateProduct = (product, t, options = {}) => {
  const errors = []
  const { 
    isNew = false, 
    currentProductId = null,
    lastChangeSource = 'productDetail',
    organizationId = null,
    userId = null,
    storeId = null
  } = options

  let jotaiStore = getDefaultStore()
  let productsByBarcodeDict = jotaiStore.get(productsByBarcodeDictAtom)

  // Image upload validation
  if ('imageUploading' in product && product.imageUploading) {
    errors.push(t('productsAndIngredients.imageStillUploadingError'))
  }

  // Required fields validation - only check if field is present or if new product
  if (isNew || 'name' in product) {
    if (!product.name || product.name.length === 0) {
      errors.push(t('productsAndIngredients.nameEmptyError'))
    }
  }

  if (isNew || 'categoryID' in product) {
    if (!product.categoryID) {
      errors.push(t('productsAndIngredients.categoryEmptyError'))
    }
  }

  if (isNew || 'supplierID' in product) {
    if (!product.supplierID) {
      errors.push(t('productsAndIngredients.supplierEmptyError'))
    }
  }

  if (isNew || 'productUnit' in product) {
    if (!product.productUnit) {
      errors.push(t('productsAndIngredients.productUnitEmptyError'))
    }
  }

  // Sale prices validation
  if ('salePrices' in product && !product.isHiddenInNewSale) {
    const hasEmptySalePrice = Object.values(product.salePrices || {}).some(price => 
      price === '' || price === undefined || price === null
    )
    if (hasEmptySalePrice) {
      errors.push(t('productsAndIngredients.salePricesEmptyError'))
    }
  }

  // Purchase price validation - skip for composite products as their price is calculated from ingredients
  if ('purchasePrice' in product && product.purchasePrice === '' && !product.isComposite) {
    errors.push(t('productsAndIngredients.purchasePriceEmptyError'))
  }

  // Composite product validation
  if ('isComposite' in product && product.isComposite && (!product.ingredients || product.ingredients.length === 0)) {
    errors.push(t('productsAndIngredients.compositeNoIngredientsError'))
  }

  let importance = parseInt(product.importance) || 1

  // Numeric values validation - only check fields that are present
  const numberValuesToCheck = []
  
  if ('purchasePrice' in product) numberValuesToCheck.push(product.purchasePrice)
  if ('packagePrice' in product) numberValuesToCheck.push(product.packagePrice)
  if ('stockThreshold' in product) numberValuesToCheck.push(product.stockThreshold)
  if ('importance' in product) numberValuesToCheck.push(importance)

  if ('salePrices' in product && !product.isHiddenInNewSale) {
    numberValuesToCheck.push(...Object.values(product.salePrices || {}))
  }

  if (isNew && 'storeStock' in product) {
    numberValuesToCheck.push(...Object.values(product.storeStock || {}))
  }

  if ('oldPrice' in product && product.oldPrice !== '') {
    numberValuesToCheck.push(product.oldPrice)
  }

  if ('activeInEcommerce' in product && product.activeInEcommerce && 'defaultQuantity' in product) {
    numberValuesToCheck.push(product.defaultQuantity)
  }

  for (let value of numberValuesToCheck) {
    if (value !== undefined && value !== '') {
      const numberAmount = parseFloat(value)
      if (isNaN(numberAmount)) {
        errors.push(t('notANumberError'))
        break
      }
    }
  }

  // Barcode validation
  if ('barcode' in product) {
    // Barcode may arrive as a NUMBER (Firestore-read mode / xlsx import) — normalize before
    // string ops or `.length`/`.startsWith` silently regenerate or throw (ATL-554)
    const barcodeStr = product.barcode != null ? String(product.barcode) : ''
    let newBarcode = barcodeStr.length > 0 ? barcodeStr : generateBarcode()
    // Check if barcode and product type match, if not - regenerate barcode
    if (barcodeStr.startsWith('20') && product.type) {
      newBarcode = generateBarcode(true)
    }
    if (barcodeStr.startsWith('21') && !product.type) {
      newBarcode = generateBarcode(false)
    }
    // check if barcode is already used — against both the raw form and the PERSISTED form:
    // formattedProduct saves parseInt(barcode) || 0, so '00123'/'123abc' persist as 123;
    // checking only the raw string would miss an existing product with barcode 123 and
    // create a stealth duplicate the scanner resolves arbitrarily (ATL-554)
    if (newBarcode && String(newBarcode).length > 0) {
      const persistedBarcode = parseInt(newBarcode, 10) || 0
      const existingProduct = productsByBarcodeDict[newBarcode] ??
        (persistedBarcode ? productsByBarcodeDict[persistedBarcode] : undefined)
      if (existingProduct && existingProduct.id !== currentProductId) {
        errors.push(t('productsAndIngredients.barcodeAlreadyUsed') + existingProduct.name)
      }
    }
    product.barcode = newBarcode
  }


  // Additional barcodes validation
  if (product.barcodes) {
    for (let code of product.barcodes) {
      if (code && String(code).length > 0) {
        const existingProduct = productsByBarcodeDict[code]
        if (existingProduct && existingProduct.id !== currentProductId) {
          errors.push(t('productsAndIngredients.barcodeAlreadyUsed') + code + ' - ' + existingProduct.name)
        }
      }
    }
  }

  // Calculate purchasePrice for composite products
  let calculatedPurchasePrice = parseFloat(product.purchasePrice) || 0
  if (product.isComposite && product.ingredients && product.ingredients.length > 0) {
    // For composite products, calculate purchasePrice from ingredients
    calculatedPurchasePrice = calculateTotalPurchasePriceFromIngredients(product.ingredients)
  }

  // Format the validated product
  const formattedProduct = isNew ? {
    // Full product object for new products
    name: product.name,
    purchasePrice: calculatedPurchasePrice,
    packagePrice: parseFloat(product.packagePrice) || 0,
    activeInStore: product.activeInStore,
    salePrices: Object.entries(product.salePrices || {}).reduce((acc, [key, value]) => {
      acc[key] = product.isHiddenInNewSale ? parseFloat(product.purchasePrice) || 0 : parseFloat(value) || 0
      return acc
    }, {}),
    // Price types structure (new)
    ...(product.priceTypes ? {
      priceTypes: product.priceTypes,
      priceTypeScopes: product.priceTypeScopes || {}
    } : {}),
    storeStock: Object.entries(product.storeStock || {}).reduce((acc, [key, value]) => {
      acc[key] = parseFloat(value) || 0
      return acc
    }, {}),
    categoryID: product.categoryID,
    supplierID: product.supplierID,
    type: product.type || false,
    trackInventory: product.trackInventory !== false, // Default to true if not explicitly set to false
    productUnit: product.productUnit,
    isService: product.isService || false,
    isComposite: product.isComposite || false,
    manufacturedBeforeSale: product.manufacturedBeforeSale || false,
    ingredients: product.ingredients || [],
    productImages: product.productImages || [],
    stockThreshold: parseFloat(product.stockThreshold) || 0,
    importance,
    barcode: parseInt(product.barcode) || 0,
    // Coerce additional barcodes to non-empty strings — the backend DTO is @IsString({each})
    // with no transform, so numeric entries (Firestore-read mode) would 400 the dual-write
    barcodes: (product.barcodes || []).filter(c => c != null && String(c).length > 0).map(c => String(c)),
    punchCardEnabled: product.punchCardEnabled || false,
    isHiddenInNewSale: product.isHiddenInNewSale || false,
    description: product.description || '',
    activeInEcommerce: product.activeInEcommerce || false,
    defaultQuantity: parseFloat(product.defaultQuantity) || 0,
    productTags: product.productTags || [],
    dailyStatID: getDailyStatID(storeId, new Date()),
    // ATL-880: the ONLY numeric on this object without a finiteness guard, and the only one that
    // still MINTED a NaN. `product.oldPrice !== ''` is true for `undefined` and for `null`, so any
    // create path that simply does not carry an old price (the xlsx import, an org-setup seed —
    // the detail form does initialise it to '') wrote `oldPrice: parseFloat(undefined)` = NaN into
    // Firestore. Firestore stores NaN; the API cannot (the generated Zod schema types it
    // `number()`), so every later sale of that product had its Supabase leg refused client-side and
    // dropped as a deterministic defect. Emit the key only when a finite number is actually there —
    // absent means "no old price", which is what the update branch below already encodes as ''.
    ...(Number.isFinite(parseFloat(product.oldPrice))
      ? { oldPrice: parseFloat(product.oldPrice) }
      : {}),
    createdAt: new Date(),
    lastChangeType: 'new',
    lastChangeBy: userId,
    lastChangeAt: new Date(),
    lastChangeSource: lastChangeSource,
    lastChangeStore: storeId,
    organizationId: organizationId
  } : {
    // Only include fields that were passed in for updates
    ...Object.keys(product).reduce((acc, key) => {
      if (key === 'salePrices') {
        acc[key] = Object.entries(product[key] || {}).reduce((priceAcc, [storeId, value]) => {
          priceAcc[storeId] = product.isHiddenInNewSale ? parseFloat(product.purchasePrice) || 0 : parseFloat(value) || 0
          return priceAcc
        }, {})
      } else if (key === 'priceTypes') {
        // Handle priceTypes structure (new)
        acc[key] = product[key]
      } else if (key === 'priceTypeScopes') {
        // Handle priceTypeScopes (new)
        acc[key] = product[key]
      } else if (['packagePrice', 'stockThreshold', 'defaultQuantity'].includes(key)) {
        acc[key] = parseFloat(product[key]) || 0
      } else if (key === 'purchasePrice' && product.isComposite && product.ingredients && product.ingredients.length > 0) {
        // For composite products, calculate purchasePrice from ingredients
        acc[key] = calculateTotalPurchasePriceFromIngredients(product.ingredients)
      } else if (key === 'purchasePrice' && !product.isComposite) {
        // For composite products, calculate purchasePrice from ingredients
        acc[key] = parseFloat(product[key]) || 0
      } else if (key === 'importance') {
        acc[key] = importance
      } else if (key === 'barcode') {
        acc[key] = parseInt(product[key]) || 0
      } else if (key === 'barcodes') {
        // Same string coercion as the create branch — backend DTO is @IsString({each})
        acc[key] = (product[key] || []).filter(c => c != null && String(c).length > 0).map(c => String(c))
      } else if (key === 'storeStock') {
        // ATL-764: NEVER copy storeStock into an update. The detail-screen form holds the
        // stock AS OF form seeding (invisible on an edit — the inputs render only for new
        // products), so passing it through rewrote the whole absolute map and silently
        // reverted every sale/supply/write-off that happened while the screen was open.
        // Stock moves only through the dedicated stock paths (updateStockManually,
        // supplies, revisions, the import's direct updateProduct call) — a product EDIT
        // has no business asserting it. The create branch (isNew) still seeds it.
      } else if (key === 'oldPrice') {
        acc[key] = product.oldPrice !== '' ? parseFloat(product[key]) || 0 : ''
      } else {
        acc[key] = product[key]
      }
      return acc
    }, {}),
    lastChangeType: 'update',
    lastChangeBy: userId,
    lastChangeAt: new Date(),
    lastChangeSource: lastChangeSource,
    lastChangeStore: storeId,
    organizationId: organizationId,
    dailyStatID: getDailyStatID(storeId, new Date())
  }

  return {
    errors,
    formattedProduct
  }
} 

export const generateBarcode = (type=false) => {
  // nevesovoy (non-weighted) - 20XXXX (prefix 20, range 200000-209999)
  // vesovoy (weighted) - 21XXXX (prefix 21, range 210000-219999)
  let jotaiStore = getDefaultStore()
  let productsDict = jotaiStore.get(productsDictAtom)

  // Define ranges based on product type
  const minBarcode = type ? 210000 : 200000
  const maxBarcode = type ? 219999 : 209999
  let biggestBarcode = minBarcode

  Object.values(productsDict).forEach((product) => {
    // Parse barcode as integer for proper numeric comparison
    const barcode = parseInt(product.barcode, 10)
    // Check if barcode is in the valid range for this type
    if (!isNaN(barcode) && barcode >= minBarcode && barcode <= maxBarcode && barcode > biggestBarcode) {
      biggestBarcode = barcode
    }
  })

  return biggestBarcode + 1
}